import { NgFor } from "@angular/common";
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  TrackByFunction
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import "@enersis/gp-components/gp-tabs";
import { TranslateModule } from "@ngx-translate/core";
import { Subject, take } from "rxjs";

export interface TabConfig<T extends string = string> {
  name: T;
  label: string;
  disabled?: boolean;
}

export type TabsVariant = "primary" | "secondary" | "pill" | null;

@Component({
  selector: "tpm-tabs",
  templateUrl: "tabs.component.html",
  styleUrls: ["tabs.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgFor, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    "[attr.data-variant]": "variant"
  }
})
export class TabsComponent {
  @Output() readonly activeTabChange = new EventEmitter<string>();
  /** @deprecated Use activeTabChange instead */
  @Output() readonly activeTabChangeInitially = new EventEmitter<string>();

  @Input({ required: true }) tabs: Array<TabConfig>;
  @Input() child: boolean = false;
  @Input() hasScroller: boolean = false;
  @Input() indicator: "bottom" | "top" = "bottom";
  @Input()
  set variant(value: TabsVariant) {
    this._variant = value ?? null;
  }
  get variant(): TabsVariant {
    return this._variant;
  }

  @Input()
  set active(value: string) {
    this._active = value;
    this._activeChange.next(value);
  }
  get active(): string {
    return this._active;
  }
  protected _active: string;

  private _variant: TabsVariant = null;
  private readonly _activeChange = new Subject<string>();

  readonly trackByFn: TrackByFunction<TabConfig> = (_, tab) => tab.name;

  constructor() {
    this._activeChange.pipe(take(1), takeUntilDestroyed()).subscribe((value) => {
      this.activeTabChangeInitially.emit(value);
    });
  }

  onActiveTabChange(event: CustomEvent<{ index: number; label: string }>): void {
    event.stopPropagation();

    const tab = this.tabs.find((_, index) => index === event.detail.index);

    this.activeTabChange.emit(tab.name);
  }
}
